import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Router from 'next/router';
import GTM from 'react-gtm-module';

import { MainApiService } from '_services';
import { STORAGE_KEYS } from '_constants/storage';
import { PAYMENT_STATUS } from '_constants/payment';
import { ROUTES } from '_constants/routes';
import { TRADEMARK_SERVICE_KEYS } from '_constants/trademarkServices';
import { LETTERS_TYPE, LOGO_CATEGORY, LOGO_USE } from '_constants/forms';
import { processResponse, processError } from '_helpers/api';
import { createLeadEventBody } from '_helpers/analytics';
import { sanitizeFileName } from '_helpers/file';

import {
  createOrderBody,
  fillOrderBodyWithSourceData,
  createOrderBeforePayment,
  parseOrder,
  getRouteByServiceKey,
} from '_helpers/order';

import {
  storeSessionValue,
  restoreSessionValue,
  clearSessionValue,
  restoreLocalValue,
} from '_helpers/storage';

import {
  refreshForm as refreshBillingInfo,
  resetForm as resetBillingInfoForm,
} from '_redux/payment/slice';

const initForm = {
  step: 0,
  maxStep: 0,
  productCode: null,
  processingOption: null,
  trademarkType: 'NAME',
  markDesc: null,
  literalElements: null,
  logoColoredVariety: true,
  colorsInLogo: null,
  logoFile: null,
  isIncludeLivingPersonName: false,
  isIncludeNicknameName: false,
  ownName: null,
  isOtherLivingName: false,
  includeTranslationWords: false,
  translation: null,
  currentTrademark: false,
  firstUseAnywhereYear: null,
  firstUseAnywhereMonth: null,
  firstUseAnywhereDay: null,
  firstUseInCommerceYear: null,
  firstUseInCommerceMonth: null,
  firstUseInCommerceDay: null,
  individualOrgOrInd: 'Individual',
  isNonUsBased: 'USBased',
  firstName: null,
  lastName: null,
  coOwners: [],
  countryOfCitizenship: 'US',
  address: null,
  city: null,
  state: null,
  zip: null,
  phone: null,
  email: null,
  orgName: null,
  orgType: null,
  otherOrgType: null,
  orgState: null,
  formationCountry: 'US',
  orgPosition: null,
  classes: [
    {
      goods: true,
      goodsCategory: null,
      descriptions: [],
      goodsServiceDetail: null,
    },
  ],
  rush: true,
  isSerialNumber: true,
  serialNumber: null,
  markOwnerFirstName: null,
  markOwnerLastName: null,
  markOwnerPhone: null,
  markOwnerEmail: null,
  isAddressSame: true,
  isNewAddress: false,
  newAddress: null,
  newCity: null,
  newState: null,
  newZip: null,
  isNewDrawing: false,
  isMarkOwnerSame: true,
  isNewMarkOwner: false,
  newMarkOwnerFirstName: null,
  newMarkOwnerLastName: null,
  newMarkOwnerDescription: null,
  isNoticeOfAllowanceCovered: true,
  classesOrGoodsNotCovered: null,
  isExtensionOnClasses: false,
  itemsCovered: null,
  itemsExtension: null,
  isSpecimenNeeded: 'NO',
  classType: 'BOTH',
  isClassesInUse: 'YES_ON_ALL',
  classesStillInUse: [
    {
      goods: true,
      goodsCategory: null,
      descriptions: [],
      goodsServiceDetail: null,
    },
  ],
  classesNoLongerInUse: [
    {
      goods: true,
      goodsCategory: null,
      descriptions: [],
      goodsServiceDetail: null,
    },
  ],
  isSpecimenFile: true,
  specimenFile: null,
  specimenUrl: null,
  isExtendedInThePast: false,
  timesDeadlineWasExtended: null,
  extensionReason: null,
  extensionReasonOther: null,
  service: TRADEMARK_SERVICE_KEYS.officeActionResponse,
  officeActionStatus: 'PROCEDURAL_ISSUE_OFFICE_ACTION_TYPE',
  statementOfUseStatus: 'TRADEMARK_IN_USE',
  logoName: null,
  slogan: null,
  businessIndustry: null,
  businessDescription: null,
  businessProducts: null,
  targetAudience: null,
  businessWebsite: null,
  lettersType: LETTERS_TYPE.AS_IT_IS,
  customersConception: null,
  logoCategory: LOGO_CATEGORY.JUST_FONT,
  logoUse: LOGO_USE.WEBSITE,
  logoUseDescription: null,
  campaignId: null,
  restored: false,
};

export const pullOrder = createAsyncThunk(
  'order/pullOrder',
  async function (
    { cid, orderId, token, redirectUrl },
    { dispatch, getState, rejectWithValue }
  ) {
    try {
      MainApiService.token = token;

      const response = await MainApiService.getOrder({ orderId });
      const result = processResponse(response);

      const { order } = getState();
      const { trademarkServiceKey } = order;

      const orderBeforePayment = createOrderBeforePayment(result, token);
      const preOrder = parseOrder(trademarkServiceKey, result, cid);

      if (!preOrder) {
        throw new Error('Parsing failed');
      }

      const { form, products, paymentStatus } = preOrder;

      if (paymentStatus === PAYMENT_STATUS.success) {
        throw new Error('Order completed');
      }

      dispatch(storeOrderBeforePayment(orderBeforePayment));
      dispatch(refreshPreOrderFlag(true));
      dispatch(refreshForm(form));
      dispatch(refreshProducts(products));

      if (form?.campaignId) {
        dispatch(refreshBillingInfo({ campaignId: form?.campaignId }));
      }

      MainApiService.token = null;
      Router.push(redirectUrl || getRouteByServiceKey(trademarkServiceKey));
      return orderBeforePayment;
    } catch (e) {
      MainApiService.token = null;
      Router.push(ROUTES.home);
      return rejectWithValue(null);
    }
  }
);

export const createOrder = createAsyncThunk(
  'order/createOrder',
  async function (params, { dispatch, getState, rejectWithValue }) {
    const { redirectUrl, file, fileType } = params || {};
    const crmSessionId = restoreLocalValue(STORAGE_KEYS.crmSessionId);
    const { general, order } = getState();
    const { device, salePageURL } = general;
    const { trademarkServiceKey, form, package: packageInfo } = order;

    const body = createOrderBody(trademarkServiceKey, form, packageInfo);

    if (!body) {
      return;
    }

    fillOrderBodyWithSourceData(body, device, salePageURL);

    try {
      const response = await MainApiService.createOrder({ crmSessionId, body });

      const result = processResponse(response);

      if (file && form.productCode && result) {
        const token = result?.token?.token;
        const orderId = result?.id;
        const productId = result?.products?.find(
          ({ code }) => code === form.productCode
        )?.id;

        dispatch(
          uploadFile({
            token,
            orderId,
            productId,
            file,
            fileType,
          })
        );
      }

      dispatch(storeOrderBeforePayment(result));
      dispatch(refreshPreOrderFlag(true));

      GTM.dataLayer(createLeadEventBody(body));

      if (redirectUrl) {
        Router.push(redirectUrl);
      }

      return result;
    } catch (e) {
      return rejectWithValue(processError(e));
    }
  }
);

export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async function (params, { dispatch, getState, rejectWithValue }) {
    const { redirectUrl } = params || {};
    const crmSessionId = restoreLocalValue(STORAGE_KEYS.crmSessionId);
    const { general, order } = getState();
    const { device, salePageURL } = general;
    const { trademarkServiceKey, form, package: packageInfo, data } = order;

    const body = createOrderBody(trademarkServiceKey, form, packageInfo);

    if (!body) {
      return;
    }

    fillOrderBodyWithSourceData(body, device, salePageURL);

    try {
      MainApiService.token = data?.token?.token;

      const response = await MainApiService.updateOrder({ crmSessionId, body });
      const rawResult = processResponse(response);
      const result =
        data?.discountCouponType && data?.tokenWithDiscount
          ? {
              ...rawResult,
              discountCouponType: data?.discountCouponType,
              tokenWithDiscount: data?.tokenWithDiscount,
            }
          : rawResult;

      dispatch(storeOrderBeforePayment(result));

      MainApiService.token = null;

      if (redirectUrl) {
        Router.push(redirectUrl);
      }

      return result;
    } catch (e) {
      MainApiService.token = null;
      const error = processError(e);
      const { status, message } = error || {};

      if (
        status === 401 ||
        status === 403 ||
        (status === 400 && message === 'Illegal order status')
      ) {
        dispatch(resetResult());
        dispatch(resetError());
        dispatch(resetForm());
        dispatch(resetPackage());
        dispatch(refreshPreOrderFlag(false));
        dispatch(resetBillingInfoForm());
        clearSessionValue(STORAGE_KEYS.businessInfo);
        clearSessionValue(STORAGE_KEYS.packageInfo);
        clearSessionValue(STORAGE_KEYS.orderBeforePayment);
        clearSessionValue(STORAGE_KEYS.billingInfo);
        clearSessionValue(STORAGE_KEYS.billingCounter);
        clearSessionValue(STORAGE_KEYS.billingHash);
      }

      return rejectWithValue(processError(e));
    }
  }
);

export const uploadFile = createAsyncThunk(
  'order/uploadFile',
  async function (
    { orderId, productId, file, fileType, token },
    { rejectWithValue }
  ) {
    try {
      const formData = new FormData();

      if (file?.name) {
        formData.append('file', file, sanitizeFileName(file.name));
      } else {
        formData.append('file', file);
      }

      MainApiService.token = token;

      const response = await MainApiService.uploadFile({
        orderId,
        productId,
        body: formData,
        fileType,
      });

      MainApiService.token = null;
      return processResponse(response);
    } catch (e) {
      MainApiService.token = null;
      return rejectWithValue(processError(e));
    }
  }
);

const orderSlice = createSlice({
  name: 'order',
  initialState: {
    trademarkServiceKey: null,
    preOrderFlag: false,
    shouldUploadFile: false,
    form: {
      ...initForm,
    },
    package: {
      products: [],
      linkedProducts: [],
    },
    file: {
      data: null,
      error: null,
      loading: false,
    },
    data: null,
    error: null,
    loading: false,
    restored: false,
  },
  reducers: {
    refreshTrademarkServiceKey(state, action) {
      storeSessionValue(STORAGE_KEYS.trademarkServiceKey, action.payload);
      state.trademarkServiceKey = action.payload;
    },
    restoreTrademarkServiceKey(state) {
      const storedTrademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );
      state.trademarkServiceKey = storedTrademarkServiceKey || null;
    },
    refreshPreOrderFlag(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedPreOrderFlag =
          restoreSessionValue(STORAGE_KEYS.preOrderFlag) || {};

        storeSessionValue(STORAGE_KEYS.preOrderFlag, {
          ...storedPreOrderFlag,
          [trademarkServiceKey]: action.payload,
        });
      }

      state.preOrderFlag = action.payload;
    },
    restorePreOrderFlag(state, action) {
      if (action.payload) {
        const storedPreOrderFlag =
          restoreSessionValue(STORAGE_KEYS.preOrderFlag) || {};

        if (storedPreOrderFlag[action.payload] !== undefined) {
          state.preOrderFlag = storedPreOrderFlag[action.payload];
        }
      }
    },
    refreshShouldUploadFile(state, action) {
      state.shouldUploadFile = action.payload;
    },
    refreshForm(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedBusinessInfo =
          restoreSessionValue(STORAGE_KEYS.businessInfo) || {};

        storeSessionValue(STORAGE_KEYS.businessInfo, {
          ...storedBusinessInfo,
          [trademarkServiceKey]: {
            ...state.form,
            ...action.payload,
          },
        });
      }

      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
    restoreForm(state, action) {
      if (action.payload) {
        const storedBusinessInfo =
          restoreSessionValue(STORAGE_KEYS.businessInfo) || {};
        const storedForm = storedBusinessInfo[action.payload];

        if (storedForm) {
          state.form = {
            ...state.form,
            ...storedForm,
            logoFile: null,
            specimenFile: null,
            restored: true,
          };
        } else {
          state.form = { ...initForm };
        }
      }
    },
    resetForm(state) {
      state.form = { ...initForm };
    },
    refreshProducts(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedPackageInfo =
          restoreSessionValue(STORAGE_KEYS.packageInfo) || {};
        const storedPackageInfoByServiceKey =
          storedPackageInfo[trademarkServiceKey] || {};

        storeSessionValue(STORAGE_KEYS.packageInfo, {
          ...storedPackageInfo,
          [trademarkServiceKey]: {
            ...storedPackageInfoByServiceKey,
            products: [...action.payload],
          },
        });
      }

      state.package.products = [...action.payload];
    },
    addToProducts(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedPackageInfo =
          restoreSessionValue(STORAGE_KEYS.packageInfo) || {};
        const storedPackageInfoByServiceKey =
          storedPackageInfo[trademarkServiceKey] || {};
        const storedProducts = storedPackageInfoByServiceKey?.products || [];

        storeSessionValue(STORAGE_KEYS.packageInfo, {
          ...storedPackageInfo,
          [trademarkServiceKey]: {
            ...storedPackageInfoByServiceKey,
            products: [
              ...storedProducts.filter((code) => code !== action.payload),
              action.payload,
            ],
          },
        });
      }

      state.package.products = [
        ...state.package.products.filter((code) => code !== action.payload),
        action.payload,
      ];
    },
    removeFromProducts(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedPackageInfo =
          restoreSessionValue(STORAGE_KEYS.packageInfo) || {};
        const storedPackageInfoByServiceKey =
          storedPackageInfo[trademarkServiceKey] || {};
        const storedProducts = storedPackageInfoByServiceKey?.products || [];

        storeSessionValue(STORAGE_KEYS.packageInfo, {
          ...storedPackageInfo,
          [trademarkServiceKey]: {
            ...storedPackageInfoByServiceKey,
            products: storedProducts.filter((code) => code !== action.payload),
          },
        });
      }

      state.package.products = state.package.products.filter(
        (code) => code !== action.payload
      );
    },
    restoreProducts(state, action) {
      if (action.payload) {
        const storedPackageInfo =
          restoreSessionValue(STORAGE_KEYS.packageInfo) || {};
        const storedPackageInfoByServiceKey =
          storedPackageInfo[action.payload] || {};
        const storedProducts = storedPackageInfoByServiceKey?.products || [];
        state.package.products = storedProducts;
      }
    },
    refreshOrderBeforePayment(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedOrderBeforePayment =
          restoreSessionValue(STORAGE_KEYS.orderBeforePayment) || {};

        storeSessionValue(STORAGE_KEYS.orderBeforePayment, {
          ...storedOrderBeforePayment,
          [trademarkServiceKey]: {
            ...(action.payload || {}),
          },
        });
      }

      state.data = action.payload;
      state.restored = true;
    },
    storeOrderBeforePayment(_, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedOrderBeforePayment =
          restoreSessionValue(STORAGE_KEYS.orderBeforePayment) || {};

        storeSessionValue(STORAGE_KEYS.orderBeforePayment, {
          ...storedOrderBeforePayment,
          [trademarkServiceKey]: {
            ...(action.payload || {}),
          },
        });
      }
    },
    restoreOrderBeforePayment(state, action) {
      if (action.payload) {
        const storedOrderBeforePayment =
          restoreSessionValue(STORAGE_KEYS.orderBeforePayment) || {};
        const storedOrderBeforePaymentByServiceKey =
          storedOrderBeforePayment[action.payload] || null;
        state.data = storedOrderBeforePaymentByServiceKey;
        state.restored = true;
      }
    },
    resetPackage(state) {
      state.package.products = [];
      state.package.linkedProducts = [];
    },
    resetResult(state) {
      state.data = null;
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadFile.pending, (state) => {
        state.file.error = null;
        state.file.loading = true;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.file.data = action.payload;
        state.file.loading = false;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.file.error = action.payload;
        state.file.loading = false;
      })
      .addCase(pullOrder.pending, (state) => {
        state.data = null;
        state.error = null;
        state.loading = true;
        state.restored = false;
      })
      .addCase(pullOrder.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(pullOrder.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(createOrder.pending, (state) => {
        state.data = null;
        state.error = null;
        state.loading = true;
        state.restored = false;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(updateOrder.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.restored = false;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const {
  refreshTrademarkServiceKey,
  restoreTrademarkServiceKey,
  refreshPreOrderFlag,
  restorePreOrderFlag,
  refreshShouldUploadFile,
  refreshForm,
  restoreForm,
  resetForm,
  refreshProducts,
  addToProducts,
  removeFromProducts,
  restoreProducts,
  refreshOrderBeforePayment,
  storeOrderBeforePayment,
  restoreOrderBeforePayment,
  resetPackage,
  resetResult,
  resetError,
} = orderSlice.actions;

export default orderSlice.reducer;
